import React, { useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import {
  KIND_LABELS, METHOD_ICONS, METHOD_LABELS, Passage, REASON_LABELS, RESULT_LABELS,
  SIGNAL_LABELS, SOURCE_LABELS, Signal, subjectOf, timeOf,
} from './sentinel';

type Step = { key: string; at: string; title: string; detail?: string; tone: 'ok' | 'bad' | 'dup' | 'info'; raw?: any };

const DECIDER: Record<string, string> = {
  device: 'lo decidió el panel con su lista',
  cloud: 'lo validó DingDong en la nube',
  guard: 'lo registró vigilancia',
  agent: 'lo decidió el equipo de la caseta',
};

/** La historia del intento: decisión, señales del hardware y lo que pasó después. */
function stepsOf(p: Passage): Step[] {
  const steps: Step[] = [];
  const signals = p.signals ?? [];

  if (p.source !== 'panel') {
    steps.push({
      key: 'decision',
      at: p.occurred_at,
      title: `${METHOD_LABELS[p.method] ?? p.method} en ${SOURCE_LABELS[p.source] ?? p.source}`,
      detail: [p.result === 'denied' && p.reason ? REASON_LABELS[p.reason] ?? p.reason : RESULT_LABELS[p.result], p.decided_by ? DECIDER[p.decided_by] : null]
        .filter(Boolean).join(' · '),
      tone: p.result === 'denied' ? 'bad' : 'ok',
    });
  }

  signals.forEach((s: Signal) => {
    const bad = ['denied', 'credential_unregistered', 'credential_expired'].includes(s.type);
    steps.push({
      key: s.uuid,
      at: s.occurred_at,
      title: SIGNAL_LABELS[s.type] ?? s.type,
      detail: [s.device?.name, s.channel ? `canal ${s.channel}` : null, s.credential?.code, s.duplicate ? 'lectura repetida' : null]
        .filter(Boolean).join(' · '),
      tone: s.duplicate ? 'dup' : bad ? 'bad' : 'ok',
      raw: s.raw,
    });
  });

  return steps.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

const offset = (from: string, at: string) => {
  const ms = new Date(at).getTime() - new Date(from).getTime();
  return ms === 0 ? 'inicio' : `+${(ms / 1000).toFixed(1)} s`;
};

export default function PassageDetail({ passage: p, loading, onClose }: { passage?: Passage; loading: boolean; onClose: () => void }) {
  if (!p) {
    return (
      <div className="cc-col cc-detail">
        <div className="cc-skel" style={{ height: 120, margin: 16 }} />
        <div className="cc-skel" style={{ height: 200, margin: 16 }} />
      </div>
    );
  }

  const who = subjectOf(p);
  const steps = stepsOf(p);
  const first = steps[0]?.at ?? p.occurred_at;
  const date = new Date(p.occurred_at).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });

  const facts: [string, React.ReactNode][] = ([
    ['Punto de acceso', p.access_point ? `${p.access_point.name} · ${KIND_LABELS[p.access_point.kind]}` : null],
    ['Zona', p.zone?.name],
    ['Dirección', p.direction === 'entry' ? 'Entrada' : p.direction === 'exit' ? 'Salida' : null],
    ['Vivienda', p.unit?.name],
    ['Residente', p.resident?.name],
    ['Vehículo', p.vehicle ? [p.vehicle.brand, p.vehicle.model, p.vehicle.color].filter(Boolean).join(' ') || p.vehicle.plate_number : null],
    ['Placa', p.plate ?? p.vehicle?.plate_number],
    ['Credencial', p.credential ? `${METHOD_LABELS[p.credential.type ?? ''] ?? 'Código'} ${p.credential.code}` : null],
    ['Pase', p.entry_pass ? `${p.entry_pass.guest_name ?? 'Invitado'} · ${p.entry_pass.short_code}` : null],
    ['Responsiva', p.access_authorization?.folio],
    ['Adeudo al pasar', p.details?.debt
      ? `${p.details.debt.overdue_fees} cuota${p.details.debt.overdue_fees === 1 ? '' : 's'} vencida${p.details.debt.overdue_fees === 1 ? '' : 's'} · $${Math.round(p.details.debt.amount).toLocaleString('es-MX')}`
      : null],
    ['Registró', p.actor?.name],
    ['Equipo', p.source_device?.name],
  ] as [string, React.ReactNode][]).filter(([, v]) => v);

  return (
    <div className="cc-col cc-detail" key={p.uuid}>
      <div className="cc-scroll">
        <div className={`cc-detail-hero ${p.result}`}>
          <button className="cc-detail-close" onClick={onClose} title="Cerrar (Esc)">
            <Ionicons name="close" size={16} color="currentColor" />
          </button>
          <span className={`cc-badge ${p.result}`} style={{ position: 'relative' }}>
            <Ionicons name={p.result === 'granted' ? 'checkmark' : p.result === 'denied' ? 'close' : 'time-outline'} size={13} color="currentColor" />
            {p.result === 'denied' && p.reason ? REASON_LABELS[p.reason] ?? p.reason : RESULT_LABELS[p.result]}
          </span>
          <h2 className="cc-display">{who.title}</h2>
          <p>{who.subtitle ? `${who.subtitle} · ` : ''}{timeOf(p.occurred_at)} · {date}</p>
          <div style={{ position: 'relative', display: 'flex', gap: 6, marginTop: 12 }}>
            {p.methods.map(m => (
              <span key={m} className="cc-chip on" style={{ cursor: 'default' }}>
                <Ionicons name={(METHOD_ICONS[m] ?? 'ellipse-outline') as any} size={13} color="currentColor" />
                {METHOD_LABELS[m] ?? m}
              </span>
            ))}
          </div>
        </div>

        <div className="cc-section">
          <h3>Datos</h3>
          <div className="cc-facts">
            {facts.map(([label, value]) => (
              <div className="cc-fact" key={label}><span>{label}</span><b>{value}</b></div>
            ))}
          </div>
          {p.guest?.comments && <div className="cc-fact" style={{ marginTop: 10 }}><span>Comentarios</span><b>{p.guest.comments}</b></div>}
          {p.details?.delinquency === 'deny' && p.result === 'granted' && (
            <div className="cc-note">
              Con la bitácora nueva encendida por completo, este acceso se habría negado por adeudo.
            </div>
          )}
        </div>

        <div className="cc-section">
          <h3>Línea de tiempo</h3>
          {loading && !p.signals ? <div className="cc-skel" style={{ margin: 0 }} /> : (
            <ul className="cc-steps">
              {steps.map((s, i) => <StepItem key={s.key} step={s} offset={offset(first, s.at)} index={i} />)}
              {p.details?.door_opened_at && !steps.some(s => s.title === SIGNAL_LABELS.door_opened) && (
                <StepItem step={{ key: 'door', at: p.details.door_opened_at, title: SIGNAL_LABELS.door_opened, tone: 'ok' }} offset={offset(first, p.details.door_opened_at)} index={steps.length} />
              )}
              {steps.length === 0 && <li className="cc-faint" style={{ fontSize: 12.5 }}>Sin señales del hardware.</li>}
            </ul>
          )}
          {p.details?.confirmed_by_device && (
            <div className="cc-faint" style={{ fontSize: 12, marginTop: 8 }}>
              <Ionicons name="shield-checkmark-outline" size={12} color="currentColor" /> El panel confirmó la apertura.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepItem({ step, offset, index }: { step: Step; offset: string; index: number }) {
  const [showRaw, setShowRaw] = useState(false);

  return (
    <li className="cc-step" style={{ animationDelay: `${index * 60}ms` }}>
      <span className={`cc-step-dot ${step.tone}`} />
      <div>
        <b>{step.title}</b>
        {step.detail && <small>{step.detail}</small>}
        {step.raw && (
          <>
            <button className="cc-link" onClick={() => setShowRaw(!showRaw)}>{showRaw ? 'Ocultar crudo' : 'Ver crudo'}</button>
            {showRaw && <pre className="cc-raw">{JSON.stringify(step.raw, null, 2)}</pre>}
          </>
        )}
      </div>
      <em className="cc-num">{offset}</em>
    </li>
  );
}
